import React, { Component } from 'react'
import { Input, Select, Tooltip } from 'antd'
import { inject, observer } from 'mobx-react'
import { commissions } from '@services/api'
import RateComp from '@pages/Clients/Agent/Commissions/CreateNewCommission/RateComp'
import MathExpr, { op } from '@pages/Clients/Agent/Commissions/CreateNewCommission/MathExpr'
import SubscriptionOptionLabel from '@pages/Clients/Agent/Commissions/CreateNewCommission/SubscriptionOptionLabel'
import { COMMISSION_SELECT_STYLES } from '@pages/Clients/Agent/Commissions/CreateNewCommission/commissionSelectStyles'
import { missingMsg } from '@pages/Clients/Agent/Commissions/utils'
import { alertPopup } from '@utils/popup'

@inject((store: any) => {
    const { authStore, subscriptionsStore } = store

    return {
        authStore,
        subscriptionsStore
    }
})
@observer
export default class DebtForm extends Component<any> {
    constructor(props) {
        super(props)
        this.state = {
            inValid: false,
            monthsToCharge: 12,
            lastCommission: '',
            reason: '',
            rate: 0,
            rateParent: 0,
            subscriptionList: []
        }
    }

    componentDidMount() {
        this.props.getAgentClientCommissions()
        this.setSubscriptionList()
        this.setLastCommission()
    }

    componentDidUpdate(prevProps) {
        const {
            info: { selectedClient, subscription }
        } = this.props

        if (
            prevProps.info.selectedClient &&
            selectedClient &&
            prevProps.info.selectedClient.client_id !== selectedClient.client_id
        ) {
            // setLastCommission/setSubscriptionList already setState → re-render; no forceUpdate needed.
            this.setLastCommission()
            this.setSubscriptionList()
        } else if (prevProps.info && prevProps.info.clientCommissions != this.props.info.clientCommissions) {
            this.setLastCommission()
        } else if (prevProps.info.subscription && subscription && prevProps.info.subscription.id !== subscription.id) {
            this.setRate()
        }
    }

    setSubscriptionList = () => {
        const {
            info: { selectedClient, subscription },
            subscriptionsStore: { subscriptions: subscriptionsFromStore }
        } = this.props
        const subscriptionList = []
        let select_id = 1
        ;(selectedClient && selectedClient.client_subscription_history
            ? selectedClient.client_subscription_history
            : []
        ).forEach(hist => {
            const foundSubscription = subscriptionsFromStore.find(({ id }) => id === hist.subscription_id)
            const found = foundSubscription ? JSON.parse(JSON.stringify(foundSubscription)) : null
            if (found) {
                found.user_subscription_id = hist.id
                found.subscription_status = hist.subscription_status
                found.paid_at = hist.paid_at
                found.select_id = select_id++
                subscriptionList.push(found)
            }
        })
        if (subscriptionList.length && !subscription) this.props.selectSubscription(subscriptionList[0].select_id)

        this.setState({ subscriptionList })
    }

    onSubscriptionSelect = selectedId => {
        const { selectSubscription } = this.props
        const { subscriptionList } = this.state
        const selectedSubscription = subscriptionList.find(({ select_id }) => select_id == selectedId)

        if (selectedSubscription) {
            selectSubscription(selectedSubscription.select_id)
        }
    }

    setLastCommission = () => {
        const {
            info: { clientCommissions }
        } = this.props

        if (clientCommissions) {
            const lastCommission = clientCommissions.find(
                ({ balance, one_time, user_subscription_id }) =>
                    balance === 'CREDIT' && (!one_time || user_subscription_id)
            )

            this.setRate(lastCommission)
            this.setState({
                monthsToCharge: lastCommission ? lastCommission.numerator || lastCommission.advanced_months || 1 : 1, // was: 12
                lastCommission: lastCommission
            })
        }
    }

    setRate = lastCommission => {
        const { productName, agentType, percents, clientSubscription, agentInfo, subscription } = this.props.info
        if (!lastCommission) lastCommission = this.state.lastCommission || {}

        if (productName) {
            let old = null
            let rate = 0
            let rateParent = 0
            let rateType = 'monthly'

            let monthlyPercent = this.safeParse(lastCommission.monthly_percent, [])
            if (monthlyPercent.length && lastCommission.payment_type == 'MONTHLY') {
                // what about old commissions with no monthly_percent ?? or are they to be added?
                rate = monthlyPercent[monthlyPercent.length - 1].percent
            } else if (lastCommission.payment_type == 'YEARLY') {
                rate = lastCommission.advanced_percent
            }

            if (!rate || (subscription && lastCommission.user_subscription_id != subscription.user_subscription_id)) {
                if ((subscription && subscription.specialSub) || lastCommission.payment_type == 'YEARLY') {
                    rateType = 'advanced'
                } else if (lastCommission.payment_type == 'MONTHLY') {
                    rateType = 'monthly'
                }

                if (percents[productName] && percents[productName].older_than && clientSubscription) {
                    if (
                        new Date(clientSubscription.paid_at).getTime() <
                        new Date(percents[productName].older_than).getTime()
                    ) {
                        old = true
                    }
                }

                rate =
                    percents[productName] &&
                    percents[productName][agentType] &&
                    percents[productName][agentType][old ? 'old' : rateType]
                        ? percents[productName][agentType][old ? 'old' : rateType]
                        : 0
            }

            if (lastCommission.current_percent) rate = lastCommission.current_percent
            if (agentType === 'sub' && lastCommission.current_parent_percent)
                rateParent = lastCommission.current_parent_percent

            this.setState({ rate, rateParent })
        }
    }

    handleRateInput = (e, key) => {
        this.setState({ [key]: e.target.value.replace(/[^0-9.]*((\d*\.?\d{0,9})).*/g, '$2') })
    }

    setMonthsToCharge = monthsToCharge => {
        this.setState({ monthsToCharge })
    }

    setReason = reason => {
        this.setState({ reason })
    }

    safeParse = (value, fallback) => {
        if (!value) return fallback
        if (typeof value !== 'string') return value
        try {
            return JSON.parse(value)
        } catch (e) {
            return fallback
        }
    }

    submit = e => {
        e.preventDefault()
        const {
            info: { agentInfo, selectedClient, subscription }
        } = this.props
        const { reason, monthsToCharge, rate, rateParent } = this.state

        let missing = []

        let payload = {
            agent_id: agentInfo.id,
            client_id: selectedClient.client_id,
            balance: 'DEBT',
            cancellation_reason: reason,
            months_to_charge: subscription.specialSub ? 1 : monthsToCharge,
            subscription_id: subscription.id,
            user_subscription_id: subscription.user_subscription_id,
            current_percent: rate,
            current_parent_percent: rateParent
        }

        let nodeNames = {
            TEXTAREA: 'TEXTAREA',
            INPUT: 'INPUT'
        }

        let fields = {
            clientAmount: 'amount',
            comment: 'comment'
        }

        // NOTE: load-bearing — reads uncontrolled fields (input-clientAmount, input-comment)
        // straight from the DOM to build payload.amount / payload.comment. Kept as-is so the
        // submitted payload format stays identical to production.
        for (let el in e.target.elements) {
            let target = e.target.elements[el]

            if (nodeNames[target.nodeName] && target.id.includes('input')) {
                let field = fields[target.id.split('-')[1]]
                payload[field] = target.value

                if (field) {
                    if (!target.value) {
                        missing.push(field == 'amount' ? 'סכום' : 'הערה')
                        target.style.border = 'solid red 1px'
                    } else {
                        target.style.border = 'solid #d9d9d9 1px'
                    }
                }
            }
        }

        if (!reason) {
            missing.push('סיבת חיוב')
        }

        if (missing.length) {
            this.setState({ inValid: true })
            const msg = missingMsg(missing)
            alertPopup({ type: 'error', title: msg, body: '' })
            return
        }

        commissions
            .createNewCommission(payload)
            .then(() => {
                const callback = () => {
                    alertPopup({ type: 'success', title: 'העמלה נוצרה בהצלחה', body: '' })
                }

                this.props.onClose(callback)
            })
            .catch(error => {
                const title =
                    error && error.response && error.response.status == 502
                        ? 'אירעה שגיאה, יש לוודא שהעמלה נוצרה באופן תקין'
                        : 'אירעה שגיאה'

                alertPopup({ type: 'error', title, body: '' })
            })
    }

    cancellationReasons = {
        NOT_INTERSTED: 'לקוח לא מעוניין',
        COMPETITORS: 'מתחרים',
        DOUBLE_SUBSCRIPTION: 'כפל מנויים',
        DENIAL_TRANSACTION: 'הכחשת עסקה',
        STO_CANCELLED: 'ביטול הוראת קבע',
        RETURNED_CHARGE: 'חיוב חוזר',
        BLOCKING_PAYMENT_METHOD: 'חסימת אמצעי תשלום',
        EXPENSIVE_PRICE: 'מחיר יקר',
        BAD_SERVICE: 'חווית שירות לא טובה',
        NOT_USED: 'לא השתמש בשירות',
        UPGRADE: 'שדרוג מינוי',
        CHANGE_SUBSCRIPTION: 'שינוי מינוי',
        OTHER: 'אחר'
    }

    render() {
        const {
            info: {
                clientAmount,
                subscription,
                // commissionRate,
                clientCommissions,
                agentType
            },
            handleFormInput
        } = this.props

        const { monthsToCharge, rate: commissionRate, subscriptionList, rateParent } = this.state

        if (!subscription) {
            return <h6 style={{ color: '#fd7200' }}>לא נמצא מינוי מתאים ליצירת חיוב</h6>
        }

        const agentAmount = subscription.specialSub
            ? Number(clientAmount) * Number(commissionRate)
            : Number(clientAmount) * Number(commissionRate) * Number(monthsToCharge)

        let activeCreditCommission
        if (clientCommissions) {
            activeCreditCommission = !!clientCommissions.find(
                ({ balance, status }) => balance === 'CREDIT' && status === 'ACTIVE'
            )
        }

        let activeDebtCommission
        if (clientCommissions) {
            activeDebtCommission = !!clientCommissions.find(
                ({ balance, status }) => balance === 'DEBT' && status === 'ACTIVE'
            )
        }

        return (
            <>
                <style>{COMMISSION_SELECT_STYLES}</style>
                {activeCreditCommission && !activeDebtCommission && (
                    <h6 style={{ alignSelf: 'center', color: '#fd7200' }}>קיים זיכוי פעיל עבור לקוח זה</h6>
                )}
                {activeDebtCommission && !activeCreditCommission && (
                    <h6 style={{ alignSelf: 'center', color: '#fd7200' }}>קיים חיוב פעיל עבור לקוח זה</h6>
                )}
                {activeCreditCommission && activeDebtCommission && (
                    <h6 style={{ alignSelf: 'center', color: '#fd7200' }}>קיימות עמלות פעילות ללקוח זה</h6>
                )}
                <form onSubmit={this.submit} style={{ ...style.column, ...style.form }}>
                    <div style={{ ...style.formLine }}>
                        <span>מינוי:</span>
                        <Select
                            className="commission-select"
                            dropdownClassName="commission-dropdown"
                            value={
                                subscription
                                    ? (subscriptionList.find(({ id }) => id === subscription.id) || {}).select_id
                                    : null
                            }
                            onChange={this.onSubscriptionSelect}
                            style={{ width: '70%' }}
                            dropdownMatchSelectWidth={false}
                        >
                            {subscriptionList.map((sub, index) => {
                                const formatDate = str => new Date(str).toLocaleDateString('he-IL').replace(/\./g, '/')

                                let start, end
                                if (sub.dates_range) {
                                    const parsed = JSON.parse(sub.dates_range || '{}')
                                    start = formatDate(parsed.start)
                                    end = formatDate(parsed.end)
                                }

                                return (
                                    <Select.Option
                                        style={{ paddingInline: '10px' }}
                                        title={sub.name}
                                        value={sub.select_id}
                                        key={index.toString() + 'a'}
                                    >
                                        <SubscriptionOptionLabel
                                            name={sub.name}
                                            subscription_status={sub.subscription_status}
                                            paid_at={sub.paid_at}
                                            extra={
                                                sub.specialSub ? (
                                                    <span
                                                        style={{
                                                            whiteSpace: 'nowrap',
                                                            color: '#888',
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        {start} - {end}
                                                    </span>
                                                ) : null
                                            }
                                        />
                                    </Select.Option>
                                )
                            })}
                        </Select>
                    </div>

                    <div style={{ ...style.formLine }}>
                        <span>{`מחיר מינוי (חודשי)`}</span>
                        <Input
                            value={clientAmount}
                            id="input-clientAmount"
                            onChange={handleFormInput}
                            style={{ ...style.inputStyle }}
                        />
                    </div>

                    <div style={{ ...style.formLine, minHeight: '37px' }}>
                        {subscription.specialSub ? (
                            ''
                        ) : (
                            <>
                                <span>מספר חודשים לחיוב</span>
                                <Select
                                    className="commission-select"
                                    dropdownClassName="commission-dropdown"
                                    value={monthsToCharge}
                                    onChange={this.setMonthsToCharge}
                                    style={{ width: '70%' }}
                                    dropdownStyle={{ textAlign: 'right' }}
                                >
                                    {new Array(30).fill(null).map((_, index) => {
                                        return (
                                            <Select.Option
                                                key={index.toString()}
                                                style={{ textAlign: 'right' }}
                                                value={index + 1}
                                            >
                                                {index + 1}
                                            </Select.Option>
                                        )
                                    })}
                                </Select>
                            </>
                        )}
                    </div>

                    <Tooltip
                        title={
                            <MathExpr
                                tokens={[
                                    { value: clientAmount },
                                    op('×'),
                                    { value: commissionRate },
                                    ...(subscription.specialSub ? [] : [op('×'), { value: monthsToCharge }])
                                ]}
                            />
                        }
                    >
                        <div style={{ ...style.formLine }}>
                            <span>{`סכום לחיוב סוכן`}</span>
                            <div style={{ width: '70%', ...style.column }}>
                                <input
                                    id="input-agentAmount"
                                    value={agentAmount.toFixed(1).replace(/\.?0*$/, '')}
                                    disabled
                                    style={{ border: 'none', background: 'none' }}
                                />
                                <RateComp
                                    rate={commissionRate}
                                    title={'לפי שיעור:'}
                                    keys={'rate'}
                                    handle={this.handleRateInput}
                                />
                                {agentType === 'sub' ? (
                                    <div style={{ marginTop: '10px' }}>
                                        <RateComp
                                            rate={rateParent}
                                            title={'שיעור אחוז אב:'}
                                            keys={'rateParent'}
                                            handle={this.handleRateInput}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </Tooltip>

                    <div style={{ ...style.formLine }}>
                        <span>סיבת חיוב</span>
                        <Select
                            className="commission-select"
                            dropdownClassName="commission-dropdown"
                            value={this.state.reason}
                            style={{ ...style.inputStyle, textAlign: 'right' }}
                            dropdownStyle={{ textAlign: 'right' }}
                            onChange={this.setReason}
                        >
                            {Object.keys(this.cancellationReasons).map((reason, index) => (
                                <Select.Option value={reason} key={index.toString()} style={{ textAlign: 'right' }}>
                                    {this.cancellationReasons[reason] || ''}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    {this.state.reason === 'OTHER' && (
                        <div style={{ ...style.formLine }}>
                            <span>פירוט</span>
                            <Input.TextArea
                                id="input-comment"
                                rows={3}
                                maxLength={250}
                                style={{ ...style.inputStyle }}
                            />
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <button style={{ ...style.button }}>אישור</button>
                    </div>
                </form>
            </>
        )
    }
}

const style = {
    column: { display: 'flex', flexDirection: 'column' },
    form: { width: '100%', margin: '0 0 30px 0' },
    line1: { display: 'flex', justifyContent: 'end' },
    formLine: { display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '15px' },
    inputStyle: { width: '70%' },
    button: {
        height: '34px',
        width: '64px',
        background: 'white',
        borderRadius: '4px',
        border: 'solid #d9d9d9 1px',
        cursor: 'pointer',
        margin: '0 10px'
    }
}
